import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight, Phone } from "lucide-react";
import { Navigation } from "../components/Navigation";
import { Footer } from "../components/Footer";
import { useLang } from "../i18n";
import { COMPANY } from "../data";

export default function Tesekkurler() {
  const { t } = useLang();
  const { state } = useLocation();
  const name = state?.name || "";
  const wa = `https://wa.me/${(COMPANY.whatsapp?.[0] || COMPANY.phone[0]).replace(/[^0-9]/g, "")}?text=${encodeURIComponent(t("contact.waMessage"))}`;

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="bg-[#0D0C0B] min-h-screen flex flex-col">
      <Navigation />
      <main className="flex-1 flex items-center justify-center px-6 pt-32 pb-24">
        <div className="max-w-2xl w-full text-center">
          <motion.div
            initial={{ scale: 0, rotate: -30 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: "spring", stiffness: 200, damping: 15, delay: 0.1 }}
            className="mx-auto mb-10 w-20 h-20 rounded-full bg-[#7BA05B]/15 ring-1 ring-[#7BA05B]/40 flex items-center justify-center"
            data-testid="thanks-check"
          >
            <span className="w-12 h-12 rounded-full bg-[#7BA05B] flex items-center justify-center">
              <Check size={26} strokeWidth={3} className="text-[#0D0C0B]" />
            </span>
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="font-sans uppercase tracking-[0.3em] text-xs text-[#7BA05B] mb-5"
          >
            {t("thanks.label")}
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            data-testid="thanks-heading"
            className="font-serif text-5xl md:text-6xl tracking-tighter text-[#F2EFEB] leading-none mb-8"
          >
            {t("thanks.heading")}<span className="italic text-[#8C8681]">{t("thanks.headingItalic")}</span>
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45 }}
            className="space-y-3 mb-12"
          >
            {name && <p className="text-[#F2EFEB] text-lg">{t("thanks.greeting", name)}</p>}
            <p data-testid="thanks-body" className="text-[#8C8681] text-base md:text-lg leading-relaxed max-w-xl mx-auto">
              {t("thanks.body")}
            </p>
            <p className="text-[#8C8681]/80 text-sm leading-relaxed max-w-md mx-auto">{t("thanks.note")}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-3"
          >
            <Link
              to="/"
              data-testid="thanks-home-btn"
              className="group inline-flex items-center gap-2 bg-[#F2EFEB] text-[#0D0C0B] hover:bg-[#C67D57] hover:text-[#F2EFEB] font-sans uppercase tracking-[0.15em] text-xs px-7 py-4 rounded-full transition-all duration-300"
            >
              {t("thanks.home")}
              <ArrowRight size={14} strokeWidth={2} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/kaplama"
              data-testid="thanks-explore-btn"
              className="inline-flex items-center gap-2 border border-white/20 hover:border-[#7BA05B] text-[#F2EFEB] font-sans uppercase tracking-[0.15em] text-xs px-7 py-4 rounded-full transition-all duration-300"
            >
              {t("thanks.explore")}
            </Link>
            <a
              href={wa}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="thanks-whatsapp-btn"
              className="inline-flex items-center gap-2 border border-[#25D366]/40 text-[#25D366] hover:bg-[#25D366]/10 font-sans uppercase tracking-[0.15em] text-xs px-7 py-4 rounded-full transition-all duration-300"
            >
              <Phone size={14} strokeWidth={2} />
              {t("thanks.whatsapp")}
            </a>
          </motion.div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
